import { Controller } from '@hotwired/stimulus';

// Legacy Ui::Dropdown accessibility for portal builder only.
// Site header nav uses shadcn DropdownMenu + Sheet (wave 3 #9–10).
export default class extends Controller {
  static targets = ['nav'];

  connect() {
    this.dropdownBindings = [];
    this.onDocumentClick = this.handleDocumentClick.bind(this);
    this.setupDropdownAccessibility();
    document.addEventListener('click', this.onDocumentClick);
  }

  toggle() {
    if (this.hasNavTarget) {
      this.navTarget.classList.toggle('hidden');
    }
  }

  disconnect() {
    this.dropdownBindings.forEach(({ root, trigger, menu, handlers }) => {
      root.removeEventListener('mouseenter', handlers.mouseEnter);
      root.removeEventListener('mouseleave', handlers.mouseLeave);
      root.removeEventListener('focusin', handlers.focusIn);
      root.removeEventListener('focusout', handlers.focusOut);
      trigger.removeEventListener('click', handlers.click);
      trigger.removeEventListener('keydown', handlers.triggerKeydown);
      menu.removeEventListener('keydown', handlers.menuKeydown);
    });

    document.removeEventListener('click', this.onDocumentClick);
  }

  setupDropdownAccessibility() {
    const dropdownRoots = this.element.querySelectorAll('.dropdown');

    dropdownRoots.forEach((root) => {
      const trigger = root.querySelector('button[aria-controls]');
      if (!trigger) return;

      const menu = root.querySelector(`#${trigger.getAttribute('aria-controls')}`);
      if (!menu) return;

      const handlers = {
        mouseEnter: () => this.setExpandedState(root, trigger, true),
        mouseLeave: () => this.setExpandedState(root, trigger, false),
        focusIn: () => this.setExpandedState(root, trigger, true),
        focusOut: () => {
          window.setTimeout(() => {
            const stillInside = root.contains(document.activeElement);
            if (!stillInside) this.setExpandedState(root, trigger, false);
          }, 0);
        },
        click: (event) => {
          event.preventDefault();
          const willExpand = trigger.getAttribute('aria-expanded') !== 'true';
          this.closeAllDropdowns();
          this.setExpandedState(root, trigger, willExpand);
        },
        triggerKeydown: (event) => {
          if (event.key === 'Escape') {
            this.setExpandedState(root, trigger, false);
            trigger.focus();
          }
        },
        menuKeydown: (event) => {
          if (event.key === 'Escape') {
            this.setExpandedState(root, trigger, false);
            trigger.focus();
          }
        }
      };

      root.addEventListener('mouseenter', handlers.mouseEnter);
      root.addEventListener('mouseleave', handlers.mouseLeave);
      root.addEventListener('focusin', handlers.focusIn);
      root.addEventListener('focusout', handlers.focusOut);
      trigger.addEventListener('click', handlers.click);
      trigger.addEventListener('keydown', handlers.triggerKeydown);
      menu.addEventListener('keydown', handlers.menuKeydown);

      this.dropdownBindings.push({ root, trigger, menu, handlers });
    });
  }

  setExpandedState(root, trigger, expanded) {
    trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    root.classList.toggle('dropdown-open', expanded);
  }

  closeAllDropdowns() {
    this.dropdownBindings.forEach(({ root, trigger }) => this.setExpandedState(root, trigger, false));
  }

  handleDocumentClick(event) {
    if (this.element.contains(event.target)) return;
    this.closeAllDropdowns();
  }
}
